import OncolensLogoNoText from '@/assets/OncolensLogoNoText.svg';
import OncolensLogoWithText from '@/assets/OncolensLogoWithText.svg';
import { MobileProfileMenu, ProfileMenu } from '@/features/profile';
import { Disclosure } from '@headlessui/react';
import { MenuIcon, XIcon } from '@heroicons/react/outline';
import { getTestSelector } from '@oncolens/oncolens-ui/lib/utilities/tests';
import clsx from 'clsx';
import * as React from 'react';
import { NavLink } from 'react-router-dom';

interface NavigationListItem {
  label: string;
  to: string;
}

const navigation: NavigationListItem[] = [
  {
    label: 'Requests',
    to: '/requests',
  },
];

function Header() {
  const activeDesktopLinkClass = 'px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-md';
  const inactiveDesktopLinkClass =
    'px-3 py-2 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-700 hover:text-white';

  const mobileActiveClassName =
    'block px-3 py-2 text-base font-medium text-white bg-gray-900 rounded-md';
  const mobileInactiveClassName =
    'block px-3 py-2 text-base font-medium text-gray-300 rounded-md hover:bg-gray-700 hover:text-white';

  const submitTicketLink =
    process.env.REACT_APP_ENV === 'prod'
      ? 'https://oncolens.freshdesk.com/support/tickets/new'
      : 'https://oncolensdev.freshdesk.com/support/home';

  return (
    <Disclosure as="nav" className="bg-gray-800 print:hidden">
      {({ open }) => (
        <>
          <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <div className="shrink-0">
                  <NavLink to="/requests" className="cursor-pointer">
                    <div className="flex items-center justify-center p-2 m-1 bg-white rounded-full lg:hidden">
                      <img className="w-auto h-8" src={OncolensLogoNoText} alt="Oncolens Logo" />
                    </div>
                  </NavLink>
                  <NavLink to="/requests" className="cursor-pointer">
                    <img
                      className="hidden w-auto h-12 lg:block"
                      src={OncolensLogoWithText}
                      alt="Oncolens Logo"
                    />
                  </NavLink>
                </div>
                <div className="hidden sm:block sm:ml-6">
                  <div className="flex space-x-4">
                    {navigation.map((navItem) => (
                      <NavLink
                        key={navItem.label}
                        to={navItem.to}
                        end
                        className={({ isActive }) =>
                          clsx(isActive ? activeDesktopLinkClass : inactiveDesktopLinkClass)
                        }
                        data-cy={getTestSelector(navItem.label, 'nav-link')}
                      >
                        {navItem.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-center">
                <div className="hidden sm:ml-6 sm:block">
                  <div className="flex items-center">
                    {/* <button
										type="button"
										className="p-1 text-gray-400 bg-gray-800 rounded-full hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
									>
										<span className="sr-only">View notifications</span>
										<BellIcon className="w-6 h-6" aria-hidden="true" />
									</button> */}
                    <ProfileMenu />
                  </div>
                </div>
                <div className="hidden sm:ml-6 sm:block">
                  <div className="flex text-white">
                    <a
                      href={submitTicketLink}
                      target={'_blank'}
                      rel="noreferrer"
                      data-cy="nav-help"
                    >
                      Help
                    </a>
                  </div>
                </div>
              </div>

              <div className="flex -mr-2 sm:hidden">
                {/* Mobile menu button */}
                <Disclosure.Button className="inline-flex items-center justify-center p-2 text-gray-400 rounded-md hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XIcon className="block w-6 h-6" aria-hidden="true" />
                  ) : (
                    <MenuIcon className="block w-6 h-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>
            </div>
          </div>

          <Disclosure.Panel className="sm:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navigation.map((navItem) => (
                <NavLink
                  key={navItem.label}
                  to={navItem.to}
                  end
                  className={({ isActive }) =>
                    clsx(isActive ? mobileActiveClassName : mobileInactiveClassName)
                  }
                >
                  {navItem.label}
                </NavLink>
              ))}
            </div>
            <div className="pt-4 pb-3 border-t border-gray-700">
              <div className="flex items-center px-5">
                <MobileProfileMenu />
                {/* <button
									type="button"
									className="p-1 ml-auto text-gray-400 bg-gray-800 rounded-full shrink-0 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
								>
									<span className="sr-only">View notifications</span>
									<BellIcon className="w-6 h-6" aria-hidden="true" />
								</button> */}
              </div>
              <div className="px-2 mt-3 space-y-1">
                {/* TODO: Add logic to open profile screen when it gets developed
								 <a 
									href="#"
									className="block px-3 py-2 text-base font-medium text-gray-400 rounded-md hover:text-white hover:bg-gray-700"
								>
									Your Profile
								</a> */}
                <a
                  href="/logout"
                  className="block px-3 py-2 text-base font-medium text-gray-400 rounded-md hover:text-white hover:bg-gray-700"
                >
                  Sign out
                </a>
                <a
                  href={submitTicketLink}
                  target={'_blank'}
                  className="block px-3 py-2 text-base font-medium text-gray-400 rounded-md hover:text-white hover:bg-gray-700"
                  rel="noreferrer"
                >
                  Help
                </a>
              </div>
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}

export { Header as default };
